import type {
  AIArchetype,
  District,
  InvestmentTier,
  LoanStructure,
  MarketPhase,
  Player,
  PropertyTile,
  TradeShareLot,
} from '../types/gameTypes';
import { DISTRICT_GROUPS } from '../types/gameTypes';
import { profileOf } from './archetypes';

/**
 * AUTONOMOUS BOT ECONOMY — pure heuristics for
 *  1) proactive bot-to-bot (B2B) bargaining with counter-offers,
 *  2) the Financial Desk routine (treasury, leverage, insurance),
 *  3) anti-leader cartels / market-hegemon coalitions.
 *
 * Everything here is side-effect free so the store actions, the React UI and
 * `scripts/verify-bot-trading.ts` all share exactly one implementation.
 */

// ---- 1. B2B trading -------------------------------------------------------

/** Base probability that a bot proactively opens a B2B negotiation on its turn. */
export const B2B_BASE_CHANCE = 0.35;
/** Escalated probability when the bot is one tile short of a monopoly or low on cash. */
export const B2B_DESPERATE_CHANCE = 0.75;
/** Counter-offer window (receiver's offered/given ratio): under the floor the bot walks away. */
export const B2B_COUNTER_BAND_MIN = 0.85;
export const B2B_COUNTER_BAND_MAX = 1.04;
/** The cash sweetener is rounded up to a clean $10 step so the ticker reads nicely. */
export const B2B_COUNTER_STEP = 10;

// ---- 3. Leader detection & cartels ---------------------------------------

/** A player is a market hegemon above this share of the table's combined net worth. */
export const LEADER_NET_WORTH_SHARE = 0.4;
/** …or once it holds this many complete colour monopolies. */
export const LEADER_MONOPOLY_COUNT = 2;
/** Strict embargo: the leader must overpay 3.5× on any neutral trade. */
export const LEADER_EMBARGO_MULTIPLIER = 3.5;
/** Trailing bots cut each other slack on a mutually beneficial swap. */
export const CARTEL_SWEETHEART_RATIO = 0.95;

// ---- 2. Financial Desk routine -------------------------------------------

/** Aggressive bots may park at most this share of their cash in the venture fund. */
export const AGGRESSIVE_VENTURE_MAX_PCT = 0.4;
/** Rival rent exposure (share of liquid cash) that triggers auto-insurance. */
export const INSURANCE_EXPOSURE_PCT = 0.35;
/** Idle-cash deposit floor per persona (cash kept liquid above the safety reserve). */
export const TREASURY_IDLE_FLOOR: Record<AIArchetype, number> = {
  CONSERVATIVE: 1200,
  OPPORTUNISTIC: 1500,
  AGGRESSIVE: 0,
};
/** Smallest tranche a proactive growth loan is worth originating. */
export const MIN_GROWTH_LOAN = 300;

// ---- Shared board maths (kept local so this module stays dependency-free) --

export const districtTilesOf = (board: PropertyTile[], district: District): PropertyTile[] =>
  board.filter((t) => t.district === district && t.tileType === 'COMMERCIAL');

export const controlsTile = (tile: PropertyTile, playerId: string): boolean =>
  (tile.shares[playerId] ?? 0) > tile.totalShares / 2;

export const unitPriceOf = (tile: PropertyTile): number =>
  (tile.valuation > 0 ? tile.valuation : tile.baseCost) / Math.max(1, tile.totalShares);

export const lotsWorth = (board: PropertyTile[], lots: TradeShareLot[]): number =>
  lots.reduce((sum, lot) => {
    const tile = board.find((t) => t.id === lot.tileId);
    return tile ? sum + lot.shares * unitPriceOf(tile) : sum;
  }, 0);

/** Every commercial colour group a player fully controls (>50 % on every tile). */
export const monopoliesOf = (board: PropertyTile[], playerId: string): District[] =>
  DISTRICT_GROUPS.filter((district) => {
    const tiles = districtTilesOf(board, district);
    return tiles.length > 0 && tiles.every((t) => controlsTile(t, playerId));
  });

export interface LeaderSnapshot {
  id: string;
  netWorth: number;
  /** Share of the table's combined net worth (0–1). */
  share: number;
  monopolies: number;
  /** True once the 40 % net-worth threshold or the 2-monopoly rule is met. */
  isLeader: boolean;
}

/**
 * Market-hegemon scan: the richest player triggers the Leader Alert when it controls
 * more than 40 % of the table's net worth OR holds 2+ complete monopolies.
 */
export const detectMarketLeader = (
  board: PropertyTile[],
  players: Player[],
  netWorthOf: (playerId: string) => number,
): LeaderSnapshot | null => {
  const alive = players.filter((p) => !p.isBankrupt);
  if (alive.length < 2) return null;
  const rows = alive.map((p) => ({
    id: p.id,
    netWorth: Math.max(0, Math.round(netWorthOf(p.id))),
    monopolies: monopoliesOf(board, p.id).length,
  }));
  const total = rows.reduce((sum, row) => sum + row.netWorth, 0);
  if (total <= 0) return null;
  const top = [...rows].sort((a, b) => b.netWorth - a.netWorth)[0];
  const share = top.netWorth / total;
  return {
    id: top.id,
    netWorth: top.netWorth,
    share,
    monopolies: top.monopolies,
    isLeader: share > LEADER_NET_WORTH_SHARE || top.monopolies >= LEADER_MONOPOLY_COUNT,
  };
};

export interface DistrictGap {
  district: District;
  /** The one tile the player does NOT yet control. */
  missing: PropertyTile;
}

/** Districts where a player controls every tile but one (one buy away from a monopoly). */
export const districtGapsOf = (board: PropertyTile[], playerId: string): DistrictGap[] => {
  const gaps: DistrictGap[] = [];
  for (const district of DISTRICT_GROUPS) {
    const tiles = districtTilesOf(board, district);
    if (tiles.length === 0) continue;
    const missing = tiles.filter((t) => !controlsTile(t, playerId));
    if (missing.length === 1) gaps.push({ district, missing: missing[0] });
  }
  return gaps;
};

/** A bot is in a cash crisis when it drops under its archetype's trade buffer. */
export const isCashCrisis = (player: Player): boolean =>
  player.cash < Math.max(300, profileOf(player.aiArchetype).trade.cashBuffer);

/** Probability the active bot opens a negotiation: 35 % idle, 75 % when desperate. */
export const botTradeChance = (board: PropertyTile[], player: Player): number =>
  districtGapsOf(board, player.id).length > 0 || isCashCrisis(player) ? B2B_DESPERATE_CHANCE : B2B_BASE_CHANCE;

export const shouldBotTrade = (board: PropertyTile[], player: Player, rng: () => number = Math.random): boolean =>
  rng() < botTradeChance(board, player);

/** One concrete B2B deal the active bot can table (swap or liquidity sale). */
export interface BotDeal {
  partnerId: string;
  /** Lots the initiator hands to the partner. */
  give: TradeShareLot[];
  /** Lots the partner hands to the initiator. */
  take: TradeShareLot[];
  /** Cash the initiator tops the deal up with. */
  offeredCash: number;
  /** Cash the partner pays the initiator (liquidity fire-sale). */
  requestedCash: number;
  giveValue: number;
  takeValue: number;
  completesInitiator: boolean;
  completesPartner: boolean;
  /** Both sides complete a district → cartel sweetheart swap. */
  mutual: boolean;
}

/** Enough shares to flip a tile to >50 % control (never more than the holder owns). */
const controlStakeOf = (held: number): number => Math.min(held, 51);

/**
 * Synergy scan: build "win-win" swaps between complementary split districts.
 * Example — A holds 2/3 Brown + 1/3 Green, B holds 1/3 Brown + 2/3 Green →
 * A gives its Green control stake for B's Brown control stake and both complete a set.
 * A cash-strapped initiator may instead offload a minority stake to raise liquidity.
 * `leaderId` activates the strict embargo: no completed district ever reaches the leader.
 */
export const findSynergyDeals = (
  board: PropertyTile[],
  players: Player[],
  initiator: Player,
  leaderId: string | null = null,
): BotDeal[] => {
  const partners = players.filter((p) => p.isAI && !p.isBankrupt && p.id !== initiator.id);
  const myGaps = districtGapsOf(board, initiator.id);
  const deals: BotDeal[] = [];

  for (const partner of partners) {
    const theirGaps = districtGapsOf(board, partner.id);
    const iGain = myGaps.filter((g) => controlsTile(g.missing, partner.id) && !g.missing.isFrozen);
    const theyGain = theirGaps.filter((g) => controlsTile(g.missing, initiator.id) && !g.missing.isFrozen);

    // ---- 1) Win-win swap: both sides complete a district ----
    // Strict cartel embargo — never hand the market leader a completed district.
    if (!(leaderId && (partner.id === leaderId || initiator.id === leaderId))) {
      let best: BotDeal | null = null;
      let bestScore = -Infinity;
      for (const gain of iGain) {
        for (const give of theyGain) {
          if (give.district === gain.district || give.missing.id === gain.missing.id) continue;
          const giveHeld = give.missing.shares[initiator.id] ?? 0;
          const takeHeld = gain.missing.shares[partner.id] ?? 0;
          const giveLots: TradeShareLot[] = [{ tileId: give.missing.id, shares: controlStakeOf(giveHeld) }];
          const takeLots: TradeShareLot[] = [{ tileId: gain.missing.id, shares: controlStakeOf(takeHeld) }];
          const giveValue = lotsWorth(board, giveLots);
          const takeValue = lotsWorth(board, takeLots);
          const offeredCash = Math.max(0, Math.round(takeValue - giveValue));
          // The initiator never overpays: prefer pairings where the incoming stake is worth more.
          const score = takeValue - giveValue - offeredCash;
          if (score > bestScore) {
            bestScore = score;
            best = {
              partnerId: partner.id,
              give: giveLots,
              take: takeLots,
              offeredCash,
              requestedCash: 0,
              giveValue,
              takeValue,
              completesInitiator: true,
              completesPartner: true,
              mutual: true,
            };
          }
        }
      }
      if (best) deals.push(best);
    }

    // ---- 2) Liquidity fire-sale: a cash-starved bot offloads a minority stake ----
    if (!isCashCrisis(initiator)) continue;
    if (leaderId && partner.id === leaderId) continue; // never feed the leader cheap paper
    const stake = board
      .map((tile) => ({ tile, owned: tile.shares[initiator.id] ?? 0 }))
      .filter(
        (h) =>
          h.owned > 0 &&
          h.owned <= 25 &&
          !h.tile.isFrozen &&
          !deals.some((d) => d.give.some((lot) => lot.tileId === h.tile.id)),
      )
      .sort((a, b) => unitPriceOf(b.tile) - unitPriceOf(a.tile))[0];
    if (!stake) continue;
    const lot: TradeShareLot[] = [{ tileId: stake.tile.id, shares: stake.owned }];
    const value = lotsWorth(board, lot);
    const price = Math.round(value * 0.85);
    if (price <= 0 || partner.cash < price + profileOf(partner.aiArchetype).trade.cashBuffer) continue;
    deals.push({
      partnerId: partner.id,
      give: lot,
      take: [],
      offeredCash: 0,
      requestedCash: price,
      giveValue: value,
      takeValue: 0,
      completesInitiator: false,
      completesPartner: false,
      mutual: false,
    });
  }

  // Mutually completing (cartel) swaps first, then the biggest swings, then liquidity.
  return deals.sort((a, b) => {
    if (a.mutual !== b.mutual) return a.mutual ? -1 : 1;
    return b.giveValue + b.takeValue - (a.giveValue + a.takeValue);
  });
};

export interface CounterOfferInput {
  /** Receiver's raw offered/given ratio (1.0 = par). */
  ratio: number;
  /** Extra cash the receiver demands to reach its accept threshold. */
  shortfall: number;
  initiatorCash: number;
  initiatorReserve: number;
  /** 3.5× when the deal touches the market leader. */
  embargoMultiplier?: number;
}

export interface CounterOfferResult {
  /** True → the initiator can afford the sweetener, the deal closes. */
  counter: boolean;
  cashAdded: number;
  /** True → the demand exceeds the initiator's reserves; the bot walks away. */
  withdrawn: boolean;
}

/**
 * Counter-offer resolution: when the ratio sits inside the 0.85×–1.04× window the
 * receiver does not reject outright — it demands a cash sweetener. The active bot then
 * re-evaluates the demand against its own reserves.
 */
export const resolveCounterOffer = (input: CounterOfferInput): CounterOfferResult => {
  const { ratio, shortfall, initiatorCash, initiatorReserve } = input;
  if (ratio < B2B_COUNTER_BAND_MIN || ratio > B2B_COUNTER_BAND_MAX) {
    return { counter: false, cashAdded: 0, withdrawn: false };
  }
  const gross = Math.max(0, shortfall) * (input.embargoMultiplier ?? 1);
  const demanded = Math.ceil(gross / B2B_COUNTER_STEP) * B2B_COUNTER_STEP;
  if (demanded <= 0) return { counter: false, cashAdded: 0, withdrawn: false };
  if (initiatorCash - demanded < initiatorReserve) return { counter: false, cashAdded: 0, withdrawn: true };
  return { counter: true, cashAdded: demanded, withdrawn: false };
};

export interface BotTradeOutcome {
  executed: boolean;
  initiatorId: string;
  partnerId: string;
  give: TradeShareLot[];
  take: TradeShareLot[];
  /** Net cash flowing from the initiator to the partner. */
  cashPaid: number;
  /** Cash sweetener the partner demanded before agreeing (>0 when a counter-offer closed it). */
  counterRequested: number;
  /** True when the deal was a cartel sweetheart swap between two trailing bots. */
  cartel: boolean;
  /** Ready-to-print ticker line. */
  message: string;
}

// ---- 2. Financial Desk routine -------------------------------------------

export interface TreasuryPlan {
  withdraw?: { tier: InvestmentTier; amount: number };
  deposit?: { tier: InvestmentTier; amount: number };
}

/**
 * Idle-cash drag management per persona:
 *  - CONSERVATIVE → Money Market Fund (3 % risk-free) above a $1,200 reserve.
 *  - OPPORTUNISTIC → Real Estate REITs above a $1,500 reserve.
 *  - AGGRESSIVE → up to 40 % of cash in the Venture Fund during a BOOM, liquidated otherwise.
 * Funds are drained the moment cash drops under the operating threshold.
 */
export const planTreasury = (
  player: Player,
  phase: MarketPhase,
  positions: { tier: InvestmentTier; value: number }[],
): TreasuryPlan => {
  const profile = profileOf(player.aiArchetype);
  const pick = (tier: InvestmentTier) => positions.find((p) => p.tier === tier);

  if (player.aiArchetype === 'AGGRESSIVE') {
    const position = pick('VENTURE');
    if (phase === 'BOOM') {
      const investable = Math.round(player.cash * AGGRESSIVE_VENTURE_MAX_PCT);
      return investable > 0 ? { deposit: { tier: 'VENTURE', amount: investable } } : {};
    }
    return position && position.value > 0 ? { withdraw: { tier: 'VENTURE', amount: position.value } } : {};
  }

  const tier = profile.treasury.tier;
  if (!tier) return {};
  const position = pick(tier);
  if (player.cash < profile.treasury.withdrawFloor && position && position.value > 0) {
    return { withdraw: { tier, amount: position.value } };
  }
  const floor = Math.max(TREASURY_IDLE_FLOOR[player.aiArchetype ?? 'OPPORTUNISTIC'], profile.treasury.idleThreshold);
  const surplus = player.cash - floor;
  return surplus > 0 ? { deposit: { tier, amount: Math.floor(surplus) } } : {};
};

export const insuranceTriggered = (worstRent: number, cash: number): boolean =>
  worstRent > cash * INSURANCE_EXPOSURE_PCT;

export interface InsuranceScan {
  worstRent: number;
  worstTile: PropertyTile | null;
}

/** Worst upcoming rival rent inside the persona's look-ahead window (rent to self ignored). */
export const insuranceScanOf = (
  board: PropertyTile[],
  player: Player,
  phase: MarketPhase,
  scanAhead: number,
  rentOf: (tile: PropertyTile, phase: MarketPhase) => number,
): InsuranceScan => {
  let worstRent = 0;
  let worstTile: PropertyTile | null = null;
  for (let step = 2; step <= scanAhead; step += 1) {
    const target = board[(player.currentTileIndex + step) % board.length];
    if (!target || target.tileType !== 'COMMERCIAL' || target.isFrozen) continue;
    if (!Object.entries(target.shares).some(([id, held]) => id !== player.id && held > 0)) continue;
    const rent = rentOf(target, phase);
    if (rent > worstRent) {
      worstRent = rent;
      worstTile = target;
    }
  }
  return { worstRent, worstTile };
};

export interface GrowthLoanPlan {
  principal: number;
  structure: LoanStructure;
  purpose: 'LANDING' | 'UPGRADE';
  tileId: string;
}

export interface GrowthLoanInput {
  player: Player;
  board: PropertyTile[];
  /** Remaining credit line (ceiling − existing debt − structured loans). */
  headroom: number;
  /** Cash price of one share in a given tile. */
  priceOf: (tile: PropertyTile) => number;
  /** Remaining public float of a tile. */
  floatOf: (tile: PropertyTile) => number;
  /** Cost of the next upgrade tier on a tile. */
  upgradeCostOf: (tile: PropertyTile) => number;
}

/**
 * Leverage for growth: aggressive & opportunistic bots draw an Annuity / Balloon loan when
 * they land on an unowned plot or spot an upgrade they cannot fund, staying inside the ceiling.
 * Prudence Banker never borrows.
 */
export const planGrowthLoan = (input: GrowthLoanInput): GrowthLoanPlan | null => {
  const { player, board, headroom } = input;
  if (player.aiArchetype === 'CONSERVATIVE') return null;
  if (headroom < MIN_GROWTH_LOAN) return null;
  const profile = profileOf(player.aiArchetype);
  const unitOf = (tile: PropertyTile) => Math.max(1, input.priceOf(tile));

  // 1) Landing on an unowned plot the bot cannot fund from cash alone.
  const landing = board[player.currentTileIndex];
  if (
    landing &&
    landing.tileType === 'COMMERCIAL' &&
    !landing.isFrozen &&
    input.floatOf(landing) > 0 &&
    (landing.shares[player.id] ?? 0) === 0
  ) {
    const targetPct = player.aiArchetype === 'AGGRESSIVE' ? 0.51 : profile.purchase.trancheMin / 100;
    const wanted = Math.max(profile.purchase.minBite, Math.ceil(landing.totalShares * targetPct));
    const gap = Math.round(wanted * unitOf(landing)) - (player.cash - profile.purchase.cashFloor);
    if (gap >= MIN_GROWTH_LOAN) {
      return {
        principal: Math.min(Math.round(gap), headroom),
        structure: player.aiArchetype === 'AGGRESSIVE' ? 'BALLOON' : 'ANNUITY',
        purpose: 'LANDING',
        tileId: landing.id,
      };
    }
  }

  // 2) An upgrade opportunity on a controlled tile that cash cannot cover.
  const upgrade = board
    .filter((t) => t.tileType === 'COMMERCIAL' && controlsTile(t, player.id) && !t.isFrozen && t.upgradeLevel < 5)
    .map((t) => ({ tile: t, cost: input.upgradeCostOf(t) }))
    .filter((entry) => entry.cost - (player.cash - profile.upgrade.fixedReserve) >= MIN_GROWTH_LOAN)
    .sort((a, b) => b.cost - a.cost)[0];
  if (upgrade) {
    const gap = upgrade.cost - player.cash;
    if (gap > 0) {
      return {
        principal: Math.min(Math.round(gap), headroom),
        structure: 'ANNUITY',
        purpose: 'UPGRADE',
        tileId: upgrade.tile.id,
      };
    }
  }
  return null;
};
